/**
 * MobileMenu Component
 *
 * Hamburger menu for mobile navigation
 * Slide-out drawer with all navigation links
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canManageTeam, canAccessSettings, canViewAllCustomers, getRoleLabel } from '../utils/permissions';

export default function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, contractor, signOut } = useAuth();
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const isActive = (path) => {
    // Exact match for these paths to avoid conflicts
    if (path === '/dashboard' || path === '/assessment') {
      return location.pathname === path;
    }
    // For other paths, check if current path starts with the link path
    return location.pathname.startsWith(path);
  };

  const menuLinkStyle = (path) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    textDecoration: 'none',
    color: isActive(path) ? '#667eea' : '#2c3e50',
    background: isActive(path) ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
    fontWeight: isActive(path) ? '600' : '500',
    fontSize: '16px',
    borderLeft: isActive(path) ? '4px solid #667eea' : '4px solid transparent',
    transition: 'all 0.2s ease'
  });

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'none', // Hidden by default, shown via media query
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: '#2c3e50',
          fontSize: '24px',
          lineHeight: 1
        }}
        className="mobile-menu-btn"
        aria-label="Toggle menu"
      >
        {isOpen ? '✕' : '☰'}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            animation: 'fadeIn 0.2s ease'
          }}
        />
      )}

      {/* Slide-out Menu */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: isOpen ? 0 : '-300px',
          width: '280px',
          height: '100vh',
          background: 'white',
          boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          transition: 'right 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}
      >
        {/* Menu Header */}
        <div style={{
          padding: '20px',
          borderBottom: '2px solid #e0e6ed',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
          <div style={{ color: 'white', fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
            {user?.full_name || user?.email}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              padding: '4px 8px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
              borderRadius: '4px',
              textTransform: 'uppercase'
            }}>
              {getRoleLabel(user)}
            </span>
            {contractor?.company_name && (
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '13px' }}>
                {contractor.company_name}
              </span>
            )}
          </div>
        </div>

        {/* Navigation Links */}
        <nav style={{ flex: 1, paddingTop: '8px' }}>
          <Link to="/dashboard" style={menuLinkStyle('/dashboard')}>
            🏠 <span>Dashboard</span>
          </Link>
          <Link to="/assessment" style={menuLinkStyle('/assessment')}>
            🔍 <span>New Assessment</span>
          </Link>
          <Link to="/assessments" style={menuLinkStyle('/assessments')}>
            📋 <span>Assessments</span>
          </Link>
          {canViewAllCustomers(user) && (
            <Link to="/customers" style={menuLinkStyle('/customers')}>
              👤 <span>Customers</span>
            </Link>
          )}
          {canManageTeam(user) && (
            <Link to="/team" style={menuLinkStyle('/team')}>
              👥 <span>Team</span>
            </Link>
          )}
          {canAccessSettings(user) && (
            <Link to="/settings" style={menuLinkStyle('/settings')}>
              ⚙️ <span>Settings</span>
            </Link>
          )}
        </nav>

        {/* Menu Footer */}
        <div style={{
          padding: '20px',
          borderTop: '2px solid #e0e6ed'
        }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '14px',
              background: '#fee2e2',
              color: '#dc2626',
              border: '2px solid #fca5a5',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            🚪 Sign Out
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
}
